/**
 * IMPORTANTE: A ordem das permissões NUNCA deve ser alterada!
 * Esta lista deve estar SINCRONIZADA com o backend (base/app/lib/permissions/bitmap.rb)
 */
export const PERMISSIONS_INDEX = [
  // RELACIONAMENTO (0-9)
  'customers:show', // 0
  'customers:manage', // 1
  'customers:archive', // 2
  'customers:export', // 3
  'customers:add_payment_method', // 4
  'customers:add_document', // 5
  'customer_groups:show', // 6
  'customer_groups:manage', // 7
  'customer_documents:show', // 8
  'customer_documents:manage', // 9

  // COMERCIAL (10-21)
  'products:show', // 10
  'products:manage', // 11
  'product_links:show', // 12
  'product_links:manage', // 13
  'register_links:show', // 14
  'register_links:manage', // 15
  'contracts:show', // 16
  'contracts:manage', // 17
  'contracts:send', // 18
  'contracts:sign', // 19
  'contract_templates:show', // 20
  'contract_templates:manage', // 21

  // FATURAMENTO (22-49)
  'billings:show', // 22
  'billings:manage', // 23
  'billings:cancel', // 24
  'billings:mark_as_paid', // 25
  'billings:send_notification', // 26
  'billings:generate_nfse', // 27
  'installments:show', // 28
  'installments:manage', // 29
  'installments:cancel', // 30
  'recurrences:show', // 31
  'recurrences:manage', // 32
  'recurrences:cancel', // 33
  'recurrences:pause', // 34
  'recurrences:resume', // 35
  'payments:show', // 36
  'payments:refund', // 37
  'nfses:show', // 38
  'nfses:manage', // 39
  'nfses:cancel', // 40
  'nfses:download', // 41
  'nfse_issuers:show', // 42
  'nfse_issuers:manage', // 43
  'negativation:show', // 44
  'negativation:manage', // 45
  'negativation:cancel', // 46
  'transfers:show', // 47
  'transfers:manage', // 48
  'transfers:cancel', // 49

  // OPERAÇÃO (50-61)
  'balance:view', // 50
  'receivables:show', // 51
  'receivables:export', // 52
  'accounts_payable:show', // 53
  'accounts_payable:manage', // 54
  'accounts_payable:pay', // 55
  'dashboard:view', // 56
  'financial_analysis:view', // 57
  'reports:show', // 58
  'reports:export', // 59
  'billing_rulers:show', // 60
  'billing_rulers:manage', // 61

  // CONFIGURAÇÕES (62-73)
  'merchant_settings:view', // 62
  'merchant_settings:manage', // 63
  'users:show', // 64
  'users:manage', // 65
  'bank_accounts:show', // 66
  'bank_accounts:manage', // 67
  'tags:show', // 68
  'tags:manage', // 69
  'custom_fields:show', // 70
  'custom_fields:manage', // 71
  'webhooks:show', // 72
  'webhooks:manage', // 73

  // PORTAL (74-77)
  'portal:view', // 74
  'portal:customize', // 75
  'announcements:show', // 76
  'announcements:manage', // 77

  // OUTROS (78-79)
  'credit_cards:show', // 78
  'credit_cards:delete', // 79
  'notifications:view', // 80
  'imports:show', // 81
  'sales_page:show', // 82
  'sales_page:manage', // 83
  'workflows:show', // 84
  'carne:export', // 85

  // CONTAS A RECEBER (86-88)
  'accounts_receivable:show', // 86
  'accounts_receivable:manage', // 87
  'accounts_receivable:receive', // 88

  // CUPONS (89-90)
  'coupons:show', // 89
  'coupons:manage', // 90

  // STATISTICS (91-97)
  'billings:statistics', // 91
  'installments:statistics', // 92
  'recurrences:statistics', // 93
  'transfers:statistics', // 94
  'product_links:statistics', // 95
  'nfses:statistics', // 96
  'negativation:statistics', // 97
  'financial_analysis:manage', // 98
  'integrations:manage', // 99

  // MÓDULOS FINANCEIROS AVANÇADOS (100-102) — SC-9544
  // Espelho de app/lib/permissions/bitmap.rb do BASE. A ordem é o contrato: o
  // backend liga o bit pelo índice, e um array mais curto aqui faz o front
  // descartar em silêncio as permissões que vieram no token.
  'cash_flow:access', // 100
  'dre:access', // 101
  'bank_reconciliation:access', // 102

  // PLANO DE RECEBIMENTO DE CARTÃO (103-104) — SC-10370
  'credit_card_plans:show', // 103
  'credit_card_plans:manage', // 104

  // MERCHANTS DO MARKETPLACE (105-106) — SC-10982
  // Não são lidas no conta hoje, mas precisam ocupar a posição que o BASE usa:
  // sem elas o índice das permissões seguintes cairia no lugar errado.
  'marketplace_merchants:show', // 105
  'marketplace_merchants:manage', // 106

  // FINANCEIRO NA FICHA DO CLIENTE (107)
  'customers:view_financial' // 107
] as const

const PERMISSION_TO_INDEX: Record<string, number> = {}
PERMISSIONS_INDEX.forEach((perm, index) => {
  PERMISSION_TO_INDEX[perm] = index
})

/**
 * Decodifica uma string base64 de bitmap para array de permissões
 *
 * @param encoded String base64 do bitmap
 * @returns Array de permissões no formato "resource:action"
 *
 * @example
 * decodePermissions("Aw==")
 * // => ["customers:show", "customers:manage"]
 */
export function decodePermissions(encoded: string): string[] {
  if (!encoded) return []

  try {
    // Decodifica base64 para bytes
    const binary = atob(encoded)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }

    // Reconstrói o bitmap (little-endian) usando BigInt para suportar > 32 bits
    let bitmap = BigInt(0)
    for (let i = bytes.length - 1; i >= 0; i--) {
      bitmap = (bitmap << BigInt(8)) | BigInt(bytes[i])
    }

    // Extrai as permissões
    return PERMISSIONS_INDEX.filter((_, index) => {
      return (bitmap & (BigInt(1) << BigInt(index))) !== BigInt(0)
    })
  } catch (error) {
    console.error('Error decoding permissions bitmap:', error)
    return []
  }
}

/**
 * Verifica se uma permissão está presente no bitmap codificado
 * Mais eficiente que decodePermissions() + includes() quando só precisa checar uma permissão
 *
 * @param encoded String base64 do bitmap
 * @param permission Permissão a verificar
 * @returns boolean
 */
export function hasPermissionFromBitmap(
  encoded: string,
  permission: string
): boolean {
  if (!encoded) return false

  const index = PERMISSION_TO_INDEX[permission]
  if (index === undefined) return false

  try {
    const byteIndex = Math.floor(index / 8)
    const bitIndex = index % 8

    const binary = atob(encoded)
    if (byteIndex >= binary.length) return false

    const byte = binary.charCodeAt(byteIndex)
    return (byte & (1 << bitIndex)) !== 0
  } catch (error) {
    console.error('Error checking permission from bitmap:', error)
    return false
  }
}

/**
 * Verifica se alguma das permissões está presente no bitmap
 */
export function hasAnyPermissionFromBitmap(
  encoded: string,
  permissions: string[]
): boolean {
  return permissions.some((perm) => hasPermissionFromBitmap(encoded, perm))
}

/**
 * Verifica se todas as permissões estão presentes no bitmap
 */
export function hasAllPermissionsFromBitmap(
  encoded: string,
  permissions: string[]
): boolean {
  return permissions.every((perm) => hasPermissionFromBitmap(encoded, perm))
}
